import React, { useState, useContext, useEffect } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import { Badge, Button, DescriptionList, EmptyState, Field, Select } from '../../../../../ui';
import type { BadgeTone } from '../../../../../ui';
import { Callout, Dialog, StudentStatusBadge } from '../ui-pending';
import { ConfigContext } from "../../../../../Api/global/ApiContext";
import { reschedule, cancelTheClass, classRecording, QaButton, generateZoomLink, airlineTeacherList, reassignAirlineTeacher, completeDemoClass, cancelDemoClass, fetchClassesCancelledByTeacher } from '../../../../../Api/apiUtils';
import { toast } from 'react-toastify';
import { MixpanelEvents,trackEvent } from '../../../../../utils/mixpanelTracking';
import { buildClassJoinUrl } from '../../../../../Api/api-constant';
import { Link } from 'react-router-dom';
import { Copy } from "react-feather";
import DemoRescheduleModal from './DemoRescheduleModal';
import { isTeacherLeaderDisabled } from '../../../../../utils/permissions';
import BookingFrequencyModal, {
  mapEventDetailsToCanonicalSlot,
} from './bookingFrequency/BookingFrequencyModal';

const DEMO_MONTH_LABELS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const formatDemoClassDate = (dateStr?: string | null): string => {
  if (!dateStr) return "—";
  const normalized = String(dateStr).split("T")[0];
  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(dateStr);
  return `${date.getDate()} ${DEMO_MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;
};

type DetailItem = { label: React.ReactNode; value: React.ReactNode };

const joinUrlItem = (classJoinUrl: string): DetailItem => ({
  label: "Join Url",
  value: (
    <span className="class-detail-inline">
      <a href={classJoinUrl} target="_blank" rel="noopener noreferrer">
        {classJoinUrl}
      </a>
      <Button
        variant="secondary"
        size="sm"
        icon={<Copy size={13} aria-hidden />}
        onClick={() => {
          navigator.clipboard.writeText(classJoinUrl);
          toast.success("Join URL copied to clipboard!");
        }}
      >
        Copy Link
      </Button>
    </span>
  ),
});

const getRecordingClassId = (event: any, storedClassId: string): string =>
  String(
    event?.classScheduleId ?? storedClassId ?? ""
    
  );

const isCompletedClass = (event: any): boolean => {
  const raw = String(
    event?.classStatus ?? event?.status ?? event?.classBookingStatus ?? ""
  ).toUpperCase();
  return raw.includes("COMPLET");
};

const isScheduledOrRescheduledClass = (event: any): boolean => {
  const raw = String(
    event?.classBookingStatus ?? event?.classStatus ?? event?.status ?? ""
  ).toUpperCase();
  if (raw.includes("DELET") || raw.includes("CANCEL")) return false;
  return raw.includes("RESCHEDULE") || raw.includes("SCHEDULE");
};

/** Show recordings for completed classes, and for demo scheduled when teacherId !== 3. */
const shouldShowClassRecording = (event: any): boolean => {
  if (!event) return false;
  if (isCompletedClass(event)) return true;
  const isDemo = String(event?.classType ?? "").toUpperCase() === "DEMO";
  const teacherId = String(
    event?.teacherId ?? event?.teacherUserId ?? event?.teacherCredentialId ?? ""
  );
  return isDemo && isScheduledOrRescheduledClass(event) && teacherId !== "3";
};

const normalizeRecordingLinks = (response: unknown): string[] => {
  const isValidRecordingLink = (value: string): boolean =>
    /^https?:\/\/.+/i.test(value.trim());

  const collectLinks = (items: unknown[]): string[] =>
    items
      .map((item) =>
        typeof item === "string" ? item : (item as { recordingUrl?: string; url?: string })?.recordingUrl ?? (item as { url?: string })?.url ?? ""
      )
      .filter((link): link is string => typeof link === "string" && isValidRecordingLink(link));

  if (!response) return [];
  if (Array.isArray(response)) return collectLinks(response);
  if (typeof response === "string" && isValidRecordingLink(response)) return [response.trim()];
  if (typeof response === "object" && response !== null) {
    const record = response as Record<string, unknown>;
    if (Array.isArray(record.recordings)) return collectLinks(record.recordings);
    if (Array.isArray(record.data)) return collectLinks(record.data);
    if (typeof record.url === "string" && isValidRecordingLink(record.url)) return [record.url.trim()];
    if (typeof record.recordingUrl === "string" && isValidRecordingLink(record.recordingUrl)) {
      return [record.recordingUrl.trim()];
    }
  }
  return [];
};

const recordingItem = (recordings: string[], loading: boolean): DetailItem => {
  if (loading) {
    return { label: "Recording", value: <span className="ds-text-subtle">Loading…</span> };
  }

  if (recordings.length === 0) {
    return { label: "Recording", value: <span className="ds-text-subtle">No recording found</span> };
  }

  return {
    label: "Recording",
    value: (
      <span className="class-detail-inline">
        {recordings.map((rec, index) => (
          <Button
            key={`${rec}-${index}`}
            variant="secondary"
            size="sm"
            icon={<Copy size={13} aria-hidden />}
            onClick={() => {
              navigator.clipboard.writeText(rec);
              toast.success("Recording link copied!");
            }}
          >
            {recordings.length > 1 ? `Copy ${index + 1}` : "Copy"}
          </Button>
        ))}
      </span>
    ),
  };
};




interface ScheduledModalProps {
  isOpen: boolean;
  toggle: () => void;
  eventDetails: any;
  studentId:any;
  courseExpiry:any

  rescheduleEvent: {
    classId: string;
    teacherName :any
    classNumber: string | number;
    classStatus: string;
    startTime: string;
    endTime: string;
    slotDate: string;
    isBooked: boolean;
    dayStats?: Record<string, { 
      workingHour: number, 
      bookedSlots: number,
      currentStudentBookedSlots: number 
    }>;
  } | null;
  onReschedule: (eventId: string, reason: string) => void;
  fetchRescheduleData: (classId: string, source: "schedule" | "reschedule") => void; 
  triggeredByScheduledButton?: boolean;
  actionSource: "schedule" | "reschedule" | null; 
  fetchCourses: () => Promise<void>;
  storedClassId: string;
  profileData:any;
  /** Teacher profile id from the selected course — not teacherUserId. */
  courseTeacherId?: string | number;
}

const ScheduledModal: React.FC<ScheduledModalProps> = ({
  isOpen,
  toggle,
  eventDetails,
  rescheduleEvent,
  fetchRescheduleData,
  onReschedule,
  triggeredByScheduledButton,
  actionSource,
  fetchCourses,
  storedClassId,
  studentId,
  courseExpiry,
  profileData,
  courseTeacherId,
}) => {
  console.log("eventDetails", eventDetails);
  const readOnly = isTeacherLeaderDisabled();

  const [selectedReason, setSelectedReason] = useState<string>("");
  const [showReasons, setShowReasons] = useState<boolean>(false);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [storedStudentId, setStoredStudentId] = useState<string>("");
  const [isCancelling, setIsCancelling] = useState<boolean>(false); 
    const [recording, setRecording] = useState<string[]>([]);
    const [recordingLoading, setRecordingLoading] = useState(false);
    const [workingHoursExceeded, setWorkingHoursExceeded] = useState(false);
    const [isScheduling, setIsScheduling] = useState(false);
    const [isRescheduling, setIsRescheduling] = useState(false);
    const [showQaModal, setShowQaModal] = useState(false);
    const [qaResponse, setQaResponse] = useState<any>(null);
    const [qaLoading, setQaLoading] = useState(false);
    const [showZoomModal, setShowZoomModal] = useState(false);
    const [zoomData, setZoomData] = useState<{startUrl: string, joinUrl: string}>({startUrl: "", joinUrl: ""});
    const [zoomLoading, setZoomLoading] = useState(false);
    const [showDemoRescheduleModal, setShowDemoRescheduleModal] = useState(false);
    const [showReassignTeacherModal, setShowReassignTeacherModal] = useState(false);
    const [reassignTeachers, setReassignTeachers] = useState<any[]>([]);
    const [reassignTeachersLoading, setReassignTeachersLoading] = useState(false);
    const [selectedReassignSlotId, setSelectedReassignSlotId] = useState<string>("");
    const [reassignSubmitting, setReassignSubmitting] = useState(false);
    const [isCompletingDemoClass, setIsCompletingDemoClass] = useState(false);
    const [showDemoCancelModal, setShowDemoCancelModal] = useState(false);
    const [isCancellingDemoClass, setIsCancellingDemoClass] = useState(false);
    const [demoTeacherCancelReasons, setDemoTeacherCancelReasons] = useState<string[]>([]);
    const [demoTeacherCancelReason, setDemoTeacherCancelReason] = useState("");
    const [demoStudentCancelReason, setDemoStudentCancelReason] = useState("");
    const [showBookingFrequency, setShowBookingFrequency] = useState(false);
    const [bookingFrequencySlots, setBookingFrequencySlots] = useState<
      ReturnType<typeof mapEventDetailsToCanonicalSlot>[]
    >([]);
    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
      return null;
    };
    const jwtToken = getCookie("jwtToken");
  
  const config = useContext(ConfigContext);

    useEffect(() => {
      if (!isOpen || !eventDetails) {
        setRecording([]);
        return;
      }

      const teacherCredentialId = eventDetails.teacherCredentialId;
      const recordingClassId = getRecordingClassId(eventDetails, storedClassId);

      if (!teacherCredentialId || !recordingClassId || !shouldShowClassRecording(eventDetails)) {
        setRecording([]);
        return;
      }

      let cancelled = false;
      const fetchClassRecording = async () => {
        setRecordingLoading(true);
        try {
          const response = await classRecording(teacherCredentialId, recordingClassId);
          if (!cancelled) {
            setRecording(normalizeRecordingLinks(response));
          }
        } catch (err) {
          console.error("Error fetching class recording:", err);
          if (!cancelled) setRecording([]);
        } finally {
          if (!cancelled) setRecordingLoading(false);
        }
      };

      void fetchClassRecording();
      return () => {
        cancelled = true;
      };
    }, [
      isOpen,
      eventDetails?.teacherCredentialId,
      eventDetails?.studentClass,
      eventDetails?.classId,
      eventDetails?.classScheduleId,
      eventDetails?.classStatus,
      eventDetails?.status,
      eventDetails?.classBookingStatus,
      eventDetails?.classType,
      eventDetails?.teacherId,
      eventDetails?.teacherUserId,
      storedClassId,
    ]);
  const handleRescheduleClick = () => {
    if (eventDetails) {
      setStoredStudentId(eventDetails.studentUserId);
      if (eventDetails.classType === "DEMO") {
        setShowDemoRescheduleModal(true);
        return;
      }
      fetchRescheduleData(eventDetails.classId, "reschedule");
 

      setShowCalendar(true);
    }
  };
  
  useEffect(() => {
    if (eventDetails && rescheduleEvent?.dayStats) {
      const date = eventDetails.slotDate || new Date().toISOString().split('T')[0];
      const stats = rescheduleEvent.dayStats[date];
      console.log("date", date)
      console.log("stats", stats)
      
      // Check if booked slots count is greater than or equal to working hours
      if (stats && stats.bookedSlots !== undefined && stats.workingHour !== undefined) {
        if (stats.bookedSlots >= stats.workingHour) {
          setWorkingHoursExceeded(true);
          console.log(`Working hours exceeded: ${stats.bookedSlots} booked slots >= ${stats.workingHour} working hours`);
        } else {
          setWorkingHoursExceeded(false);
          console.log(`Working hours not exceeded: ${stats.bookedSlots} booked slots < ${stats.workingHour} working hours`);
        }
      } else {
        setWorkingHoursExceeded(false);
        console.log("Stats not available or incomplete");
      }
    }
  }, [eventDetails, rescheduleEvent]);
  useEffect(() => {
    if (!isOpen) {
      setSelectedReason("");
      setShowReasons(false);
      setShowCalendar(false);
      setIsCancelling(false);
      setStoredStudentId("");
    }
  }, [isOpen]);
  const handleConfirmReschedule = async () => {
    setIsRescheduling(true);
    const storedStudentId = localStorage.getItem("studentUserId") ?? "";
    if (eventDetails && selectedReason) {
      const payload = {
        studentUserId: storedStudentId,
        classId: storedClassId,
        reason: selectedReason,
        selectedSlots: [{ teacherSlotInfoId: eventDetails.slotId }],
      };

      trackEvent(MixpanelEvents.RESCHEDULE_CLASS, {
        page: "Student Calendar Modal",
        studentUserId: eventDetails.studentUserId,
        courseName:eventDetails.courseName,


        classId: storedClassId,
        reason: selectedReason,
        teacherName: eventDetails.teacherName,
        teacherUserId: eventDetails.teacherUserId,
        selectedSlots: [{ teacherSlotInfoId: eventDetails.slotId }],
      });
      try {
        await reschedule(payload);
        toast.success("Rescheduled successfully");
        setShowReasons(false);
        setShowCalendar(false);
        setIsCancelling(false);
        setSelectedReason("");
        toggle();
        onReschedule(eventDetails.classId, selectedReason);
      } catch (error: any) {
        const apiData = error?.response?.data;
        const apiMessage =
          (typeof apiData === "string" && apiData.trim()) ||
          apiData?.message ||
          apiData?.error ||
          error?.message ||
          "Error rescheduling";
        toast.error(apiMessage);
      } finally {
        setIsRescheduling(false);
      }
    }
  };

  const handleCancelClick = () => {
    setShowReasons(true); // Show reasons for cancellation
    setIsCancelling(true); // Set cancellation flow to true
  };

  const getCurrentClassScheduleId = () =>
    String(eventDetails?.classScheduleId || storedClassId || "");

  const getTeacherDisplayName = (entry: any) => {
    const user = entry?.teacher?.userDetails || {};
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
    return fullName || entry?.teacher?.name || entry?.teacher?.id || entry?.id;
  };

  const openReassignTeacherModal = async () => {
    const classScheduleId = getCurrentClassScheduleId();
    if (!classScheduleId) {
      toast.error("Class schedule id is missing.");
      return;
    }
    setShowReassignTeacherModal(true);
    setReassignTeachersLoading(true);
    setSelectedReassignSlotId("");
    try {
      const data = await airlineTeacherList(classScheduleId);
      const list = Array.isArray(data) ? data : Array.isArray(data?.content) ? data.content : [];
      setReassignTeachers(list);
    } catch (error) {
      toast.error("Unable to fetch teacher list.");
      setReassignTeachers([]);
    } finally {
      setReassignTeachersLoading(false);
    }
  };

  const handleConfirmReassignTeacher = async () => {
    const classScheduleId = getCurrentClassScheduleId();
    if (!classScheduleId) {
      toast.error("Class schedule id is missing.");
      return;
    }
    if (!selectedReassignSlotId) {
      toast.error("Please select teacher.");
      return;
    }
    trackEvent(MixpanelEvents.DEMO_REASSIGN_TEACHER, {
      action: "attempted",
      classScheduleId,
      classId: eventDetails?.classId,
      airlineTeacherSlot: selectedReassignSlotId,
    });
    setReassignSubmitting(true);
    try {
      await reassignAirlineTeacher(classScheduleId, selectedReassignSlotId);
      trackEvent(MixpanelEvents.DEMO_REASSIGN_TEACHER, {
        action: "success",
        classScheduleId,
        classId: eventDetails?.classId,
        airlineTeacherSlot: selectedReassignSlotId,
      });
      toast.success("Teacher reassigned successfully.");
      setShowReassignTeacherModal(false);
      toggle();
      await onReschedule(eventDetails?.classId, "Teacher Reassigned");
      fetchCourses();
    } catch (error: any) {
      const responseError = error?.response?.data;
      const message =
        responseError?.message ||
        (typeof responseError === "string" ? responseError : "") ||
        "Failed to reassign teacher.";
      trackEvent(MixpanelEvents.DEMO_REASSIGN_TEACHER, {
        action: "failed",
        classScheduleId,
        classId: eventDetails?.classId,
        airlineTeacherSlot: selectedReassignSlotId,
        error: typeof responseError === "string" ? responseError : JSON.stringify(responseError || {}),
      });
      toast.error(message);
    } finally {
      setReassignSubmitting(false);
    }
  };

  const handleCompleteDemoClass = async () => {
    const classScheduleId = getCurrentClassScheduleId();
    const teacherId = eventDetails?.teacherUserId || eventDetails?.teacherId || eventDetails?.teacherCredentialId;
    if (!classScheduleId || !teacherId) {
      toast.error("Missing class or teacher details for completion.");
      return;
    }
    trackEvent(MixpanelEvents.DEMO_COMPLETE_CLASS, {
      action: "attempted",
      classScheduleId,
      classId: eventDetails?.classId,
      teacherId,
    });
    setIsCompletingDemoClass(true);
    try {
      await completeDemoClass(classScheduleId, teacherId);
      trackEvent(MixpanelEvents.DEMO_COMPLETE_CLASS, {
        action: "success",
        classScheduleId,
        classId: eventDetails?.classId,
        teacherId,
      });
      toast.success("Class marked as completed.");
      toggle();
      await onReschedule(eventDetails?.classId, "Completed");
      fetchCourses();
    } catch (error: any) {
      const responseError = error?.response?.data;
      const message =
        responseError?.message ||
        (typeof responseError === "string" ? responseError : "") ||
        JSON.stringify(responseError || {}) ||
        "Failed to complete class.";
      trackEvent(MixpanelEvents.DEMO_COMPLETE_CLASS, {
        action: "failed",
        classScheduleId,
        classId: eventDetails?.classId,
        teacherId,
        error: typeof responseError === "string" ? responseError : JSON.stringify(responseError || {}),
      });
      toast.error(message);
    } finally {
      setIsCompletingDemoClass(false);
    }
  };

  const normalizeStringList = (input: any): string[] => {
    const raw = Array.isArray(input)
      ? input
      : Array.isArray(input?.content)
      ? input.content
      : Array.isArray(input?.data)
      ? input.data
      : [];
    return raw
      .map((item: any) => {
        if (typeof item === "string") return item;
        return item?.reason || item?.name || item?.value || item?.label || "";
      })
      .filter((x: string) => Boolean(x));
  };

  const openDemoCancelModal = async () => {
    const teacherId = eventDetails?.teacherUserId || eventDetails?.teacherId || eventDetails?.teacherCredentialId;
    if (!teacherId) {
      toast.error("Teacher id is missing.");
      return;
    }
    setShowDemoCancelModal(true);
    setDemoTeacherCancelReason("");
    setDemoStudentCancelReason("");
    try {
      const data = await fetchClassesCancelledByTeacher(teacherId);
      setDemoTeacherCancelReasons(normalizeStringList(data));
    } catch (error: any) {
      const responseError = error?.response?.data;
      const message =
        responseError?.message ||
        (typeof responseError === "string" ? responseError : "") ||
        "Unable to fetch teacher cancellation reasons.";
      toast.error(message);
      setDemoTeacherCancelReasons([]);
    }
  };

  const handleConfirmDemoCancelClass = async () => {
    const classScheduleId = getCurrentClassScheduleId();
    const teacherId = eventDetails?.teacherUserId || eventDetails?.teacherId || eventDetails?.teacherCredentialId;
    if (!classScheduleId || !teacherId) {
      toast.error("Missing class or teacher details for cancellation.");
      return;
    }
    // if (!demoTeacherCancelReason) {
    //   toast.error("Please select teacher cancellation reason.");
    //   return;
    // }
    if (!demoStudentCancelReason) {
      toast.error("Please select student cancellation reason.");
      return;
    }
    trackEvent(MixpanelEvents.DEMO_CANCEL_CLASS, {
      action: "attempted",
      classScheduleId,
      classId: eventDetails?.classId,
      teacherId,
      cancelst: demoTeacherCancelReason,
      cancellationReasonByStudent: demoStudentCancelReason,
    });
    setIsCancellingDemoClass(true);
    try {
      await cancelDemoClass(classScheduleId, teacherId, demoTeacherCancelReason, demoStudentCancelReason);
      trackEvent(MixpanelEvents.DEMO_CANCEL_CLASS, {
        action: "success",
        classScheduleId,
        classId: eventDetails?.classId,
        teacherId,
      });
      toast.success("Class cancelled successfully.");
      setShowDemoCancelModal(false);
      toggle();
      await onReschedule(eventDetails?.classId, "Cancelled");
      fetchCourses();
    } catch (error: any) {
      const responseError = error?.response?.data;
      const message =
        responseError?.message ||
        (typeof responseError === "string" ? responseError : "") ||
        JSON.stringify(responseError || {}) ||
        "Failed to cancel class.";
      trackEvent(MixpanelEvents.DEMO_CANCEL_CLASS, {
        action: "failed",
        classScheduleId,
        classId: eventDetails?.classId,
        teacherId,
        error: typeof responseError === "string" ? responseError : JSON.stringify(responseError || {}),
      });
      toast.error(message);
    } finally {
      setIsCancellingDemoClass(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (eventDetails && selectedReason) {
      const payload = {
        studentUserId:eventDetails.studentUserId,
        classId: eventDetails.classId,
        reason: selectedReason,
      };

      trackEvent(MixpanelEvents.CANCEL_CLASS, {
        page:"Student Calendar Modal",
        studentUserId: eventDetails.studentUserId,
        classId: storedClassId,
        actionBy:"student",
        reason: selectedReason,
        teacherName:eventDetails.teacherName,
        teacherUserId:eventDetails.teacherUserId,
        selectedSlots: [{ teacherSlotInfoId: eventDetails.
          slotId
           }],
       });

      try {
        await cancelTheClass(payload);
        toast.success("Cancelled successfully")
        await onReschedule(eventDetails.classId, selectedReason);
        setShowReasons(false);
        setShowCalendar(false); 
        setIsCancelling(false); 
        setSelectedReason(""); 
        toggle();
        fetchCourses();

   

 
      } catch (error) {
        toast.error("Error cancelling");
      }
    }
  };

  const handleScheduleClick = async () => {
    if (!eventDetails) {
      console.error("Missing event details.");
      return;
    }

    const canonicalSlot = mapEventDetailsToCanonicalSlot(eventDetails);
    if (!canonicalSlot) {
      toast.error("Selected slot is missing date, time, or slot id.");
      return;
    }

    const courseId = localStorage.getItem("selectedCourse") ?? "";
    const studentUserId =
      localStorage.getItem("studentUserId") ||
      eventDetails.studentUserId ||
      profileData?.userId ||
      profileData?.uId ||
      "";
    const teacherUserId =
      eventDetails.teacherUserId ||
      eventDetails.teacherId ||
      localStorage.getItem("teacherUserId") ||
      "";

    if (!studentId || !studentUserId || !courseId || !teacherUserId) {
      toast.error("Missing student, teacher, or course details for booking.");
      return;
    }

    setBookingFrequencySlots([canonicalSlot]);
    setShowBookingFrequency(true);
    toggle();
  };

  const handleBookingFrequencySuccess = async () => {
    await onReschedule(eventDetails?.classId, "Scheduled");
    await fetchCourses();
  };

  const handleQaButton = () => {
    if (!eventDetails) return;

    // Temporarily just show info in the QA modal instead of calling API
    setQaLoading(false);
    setQaResponse({
      status: "info",
      message:
        "QA report generation from this modal is temporarily unavailable while we update the service.",
    });
    setShowQaModal(true);
  };

  const openQaAnalysis = (path: "/pages/qa-analysis" | "/pages/ld-qa-analysis") => {
    const classId = eventDetails?.classId || "";
    if (classId && eventDetails) {
      const profileTeacherId =
        courseTeacherId ||
        localStorage.getItem("SelectedCourseTeacherId") ||
        eventDetails.teacherId;
      const enhancedEventDetails = {
        ...eventDetails,
        studentName: profileData?.firstName
          ? [profileData.firstName, profileData.lastName].filter(Boolean).join(" ").trim()
          : eventDetails.studentName,
        ageGroup: profileData?.ageGroup || eventDetails.ageGroup || "",
        classNumber: eventDetails.classNumber,
        studentEmail: profileData?.emailId || "",
        studentPhone: profileData?.countryCode + profileData?.mobileNumber || "",
        gender: profileData?.gender || "",
        studentId: eventDetails.studentId || studentId,
        // teacherQAScore expects teacher profile id; student class events often store userId in teacherId
        teacherId: profileTeacherId,
        teacherUserId: eventDetails.teacherUserId || eventDetails.teacherId,
      };
      localStorage.setItem(`qaEventDetails_${classId}`, JSON.stringify(enhancedEventDetails));
    }
    window.open(
      `${window.location.origin}${process.env.PUBLIC_URL || ""}${path}?classId=${classId}`,
      "_blank"
    );
  };

  const handleZoomLinkButton = async () => {
    if (!eventDetails || !storedClassId) return;
    
    setZoomLoading(true);
    try {
      const response = await generateZoomLink(storedClassId);
      // Extract both startUrl (Teacher URL) and joinUrl (Student URL) from the response object
      const startUrl = response?.startUrl || "";
      const joinUrl = response?.joinUrl || "";
      
      setZoomData({ startUrl, joinUrl });
      
      if (startUrl || joinUrl) {
        setShowZoomModal(true);
      } else {
        toast.error("No zoom links found in response");
      }
    } catch (err: any) {
      console.error("Error generating zoom link:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to generate zoom link";
      toast.error(errorMessage);
    } finally {
      setZoomLoading(false);
    }
  };

  const parseDate = (dateStr: string) => new Date(dateStr.replace(' ', 'T'));

  const isDemoClass = eventDetails?.classType === "DEMO";
  const demoClassDate = eventDetails?.classDate ?? eventDetails?.slotDate;
  const demoStartTime = eventDetails?.classStartTime ?? eventDetails?.startTime;
  const demoEndTime = eventDetails?.classEndTime ?? eventDetails?.endTime;
  const demoCallStatus =
    eventDetails?.classBookingStatus ??
    eventDetails?.classStatus ??
    eventDetails?.status ??
    "—";
  const classJoinUrl = buildClassJoinUrl(storedClassId, isDemoClass);
  const eventTeacherId = String(
    eventDetails?.teacherId ??
      eventDetails?.teacherUserId ??
      eventDetails?.teacherCredentialId ??
      ""
  );
  const shouldShowJoinUrl = eventTeacherId !== "3";
  const rawClassStatus = String(
    eventDetails?.classBookingStatus ??
      eventDetails?.classStatus ??
      eventDetails?.status ??
      ""
  ).toUpperCase();
  const isDeletedOrCancelled =
    rawClassStatus.includes("DELET") || rawClassStatus.includes("CANCEL");
  const isScheduledOrRescheduled =
    !isDeletedOrCancelled &&
    (rawClassStatus.includes("RESCHEDULE") || rawClassStatus.includes("SCHEDULE"));
  // Demo NoShow (e.g. DEMO-NoShow) should still allow Reschedule on student demo calendar
  const isDemoNoShow =
    isDemoClass &&
    (rawClassStatus.includes("NOSHOW") ||
      rawClassStatus.includes("NO-SHOW") ||
      rawClassStatus.includes("NO_SHOW"));
  // Reschedule: scheduled, cancelled/deleted, or demo no-show; reassign stays on active scheduled demos
  const canRescheduleOrReassign =
    isScheduledOrRescheduled || isDeletedOrCancelled || isDemoNoShow;
  const callStatusDisplay = (() => {
    const booking = String(eventDetails?.classBookingStatus ?? "").toUpperCase();
    const normalized = String(eventDetails?.classStatus ?? "").toUpperCase();
    const status = String(eventDetails?.status ?? "").toUpperCase();
    if (
      booking.includes("DELET") ||
      booking.includes("CANCEL") ||
      normalized.includes("DELET") ||
      normalized.includes("CANCEL") ||
      status.includes("DELET") ||
      status.includes("CANCEL")
    ) {
      return "Cancelled";
    }
    return String(demoCallStatus);
  })();

  const slotDateTime = (eventDetails?.slotDate || eventDetails?.classDate) &&
    (eventDetails?.startTime || eventDetails?.classStartTime)
    ? new Date(
        `${eventDetails?.slotDate ?? eventDetails?.classDate}T${
          eventDetails?.startTime ?? eventDetails?.classStartTime
        }`
      )
    : null;
  const expiryDateTime = courseExpiry ? parseDate(courseExpiry) : null;
  const isCourseExpired = expiryDateTime && slotDateTime && slotDateTime > expiryDateTime;
  const readOnlyTitle = readOnly ? "Disabled for Teacher Leaders" : undefined;
  const detailStatusText = eventDetails
    ? String(eventDetails.classStatus || "").toUpperCase().includes("DELET")
      ? "Cancelled"
      : String(eventDetails.classStatus || "")
            .toUpperCase()
            .replace(/[\s_-]+/g, "")
            .includes("NOTCONDUCT")
      ? "Not Conducted"
      : (eventDetails.classStatus
      || (eventDetails.specialSlot ? "Special Free slot" : "Free slot"))
    : "";
  const qaStatusTone: BadgeTone =
    qaResponse?.status === "done" ? "success" : qaResponse?.status === "ongoing" ? "warning" : "neutral";

  return (
   <div>
    {!eventDetails?.title && (
       <Dialog
         isOpen={
           isOpen &&
           !showDemoCancelModal &&
           !showReassignTeacherModal &&
           !showDemoRescheduleModal &&
           !showZoomModal &&
           !showQaModal
         }
         onClose={toggle}
         size={isDemoClass ? "lg" : "md"}
         className="student-calendar-event-modal"
         title="Class Details"
         actions={
           <>
       {eventDetails &&
   canRescheduleOrReassign &&
   !showCalendar && (
    <Button variant="accent" className="student-calendar-event-modal__action" hidden={showReasons} disabled={readOnly} title={readOnlyTitle} onClick={handleRescheduleClick}>Reschedule Class</Button>
 )}

 {eventDetails &&
   isScheduledOrRescheduled &&
  eventDetails?.classType !== "DEMO" &&
   !showCalendar && (
    <Button variant="danger" className="student-calendar-event-modal__action" hidden={showReasons} disabled={readOnly} title={readOnlyTitle} onClick={handleCancelClick}>Cancel Class</Button>
 )}

{eventDetails &&
  eventDetails?.classType === "DEMO" &&
  isScheduledOrRescheduled &&
  !showCalendar && (
    <Button variant="secondary" className="student-calendar-event-modal__action" hidden={showReasons} disabled={readOnly} title={readOnlyTitle} onClick={openReassignTeacherModal}>
      Reassign with different teacher
    </Button>
)}

{eventDetails &&
  eventDetails?.classType === "DEMO" &&
  isScheduledOrRescheduled &&
  !showCalendar && (
    <Button
      variant="primary"
      className="student-calendar-event-modal__action"
      hidden={showReasons}
      disabled={readOnly || isCompletingDemoClass}
      loading={isCompletingDemoClass}
      title={readOnlyTitle}
      onClick={() => void handleCompleteDemoClass()}
    >
      {isCompletingDemoClass ? "Completing..." : "Complete Class"}
    </Button>
)}

{eventDetails &&
  eventDetails?.classType === "DEMO" &&
  isScheduledOrRescheduled &&
  !showCalendar && (
    <Button
      variant="danger"
      className="student-calendar-event-modal__action"
      hidden={showReasons}
      disabled={readOnly}
      title={readOnlyTitle}
      onClick={() => void openDemoCancelModal()}
    >
      Cancel Class
    </Button>
)}

 {actionSource === "schedule" && eventDetails?.isBooked === false && (
   <Button
     variant="primary"
     className="student-calendar-event-modal__action"
     hidden={Boolean(isCourseExpired ?? false)}
     onClick={handleScheduleClick}
     disabled={isScheduling || readOnly}
     loading={isScheduling}
     title={readOnlyTitle}
   >
     {isScheduling ? "Scheduling..." : "Schedule Class"}
   </Button>
 )}
         { actionSource === "reschedule" && eventDetails?.isBooked === false &&  (
          <Button variant="primary" className="student-calendar-event-modal__action" hidden={showReasons} disabled={readOnly} title={readOnlyTitle} onClick={() => setShowReasons(true)}>Reschedule Class</Button>
         )}

         {showReasons && isCancelling &&  (
          <Button variant="danger" className="student-calendar-event-modal__action" disabled={!selectedReason || readOnly} onClick={handleConfirmCancel}>
             Confirm Cancel
           </Button>
         )}

         {showReasons && !isCancelling && (
          <Button
            variant="primary"
            className="student-calendar-event-modal__action"
            disabled={!selectedReason || isRescheduling || readOnly}
            loading={isRescheduling}
            onClick={handleConfirmReschedule}
          >
             {isRescheduling ? "Rescheduling..." : "Confirm Reschedule"}
           </Button>
         )}
           </>
         }
       >

       {workingHoursExceeded && (
         <Callout tone="danger">Working hours exceeded for this date</Callout>
       )}

         {eventDetails && (
           isDemoClass ? (
             <DescriptionList
               items={[
                 { label: "Course title", value: eventDetails.courseName },
                 { label: "Class title", value: eventDetails.classTitle },
                 { label: "Description", value: eventDetails.classDescription },
                 { label: "Session Number", value: eventDetails.classNumber },
                 { label: "Class Date", value: formatDemoClassDate(demoClassDate) },
                 {
                   label: "Class Start-End Time",
                   value:
                     demoStartTime || demoEndTime
                       ? `${demoStartTime || "—"} - ${demoEndTime || "—"}`
                       : "—",
                 },
                 { label: "Teacher name", value: eventDetails.teacherName },
                 {
                   label: "Call status",
                   value:
                     callStatusDisplay === "—" ? (
                       "—"
                     ) : (
                       <StudentStatusBadge status={callStatusDisplay} label={callStatusDisplay} />
                     ),
                 },
                 ...(shouldShowClassRecording(eventDetails)
                   ? [recordingItem(recording, recordingLoading)]
                   : []),
                 ...(shouldShowJoinUrl && isScheduledOrRescheduled ? [joinUrlItem(classJoinUrl)] : []),
               ]}
             />
           ) : (
             <>
               <DescriptionList
                 items={[
                   {
                     label: "Status",
                     value: <StudentStatusBadge status={String(detailStatusText)} label={detailStatusText} />,
                   },
                   { label: "Start Time", value: eventDetails.startTime },
                   { label: "End Time", value: eventDetails.endTime },
                   { label: "Student Join Time", value: eventDetails.studentJoinTime },
                   { label: "Student Left Time", value: eventDetails.studentLeftTime },
                   { label: "Teacher Join Time", value: eventDetails.teacherJoinTime },
                   { label: "Teacher Left Time", value: eventDetails.teacherLeftTime },
                   { label: "Slot Date", value: eventDetails.slotDate },
                   { label: "Course Name", value: eventDetails.courseName },
                   { label: "Lesson Number", value: eventDetails.lessonNumber },
                   { label: "Teacher Name", value: eventDetails.teacherName },
                   { label: "Class Number", value: eventDetails.classNumber },
                   ...(isCompletedClass(eventDetails) ? [recordingItem(recording, recordingLoading)] : []),
                   ...(jwtToken && eventDetails?.classStatus === "COMPLETED"
                     ? [
                         {
                           label: "Class report",
                           value: (
                             <Link
                               className={`ds-btn ds-btn--secondary ds-btn--sm${readOnly ? " class-detail-link--disabled" : ""}`}
                               to={readOnly ? "#" : `http://betadashboard.artiumacademy.com/class-report?classId=${storedClassId}&studentId=${studentId}`}
                               target={readOnly ? undefined : "_blank"}
                               rel="noopener noreferrer"
                               aria-disabled={readOnly || undefined}
                               onClick={(e) => {
                                 if (readOnly) e.preventDefault();
                               }}
                               title={readOnlyTitle}
                             >
                               Open report
                             </Link>
                           ),
                         },
                       ]
                     : []),
                   ...(shouldShowJoinUrl && isScheduledOrRescheduled ? [joinUrlItem(classJoinUrl)] : []),
                 ]}
               />

{eventDetails?.classStatus === "COMPLETED" && eventDetails?.classType !== "DEMO" && (
  <div className="class-detail-qa">
    <Button variant="secondary" onClick={() => openQaAnalysis("/pages/qa-analysis")}>
      Academic QA
    </Button>
    <Button variant="secondary" onClick={() => openQaAnalysis("/pages/ld-qa-analysis")}>
      L&D QA
    </Button>
  </div>
)}
             </>
           )
         )}

         {showReasons && config?.classCancellationReasonsForAdmin && (
           <FormGroup tag="fieldset" className="mb-0">
             <legend className="ds-field__label">Select a reason:</legend>
             {config?.classCancellationReasonsForAdmin?.map((reasonObj: any, index: any) => (
               <FormGroup key={reasonObj.id || index} check>
                 <Label check>
                   <Input
                            className="levelChk"
                     type="radio"
                     name="cancelReason"
                     value={reasonObj.reason}
                     onChange={(e) => setSelectedReason(e.target.value)}
                   />
                   {reasonObj.reason}
                 </Label>
               </FormGroup>
             ))}
           </FormGroup>
         )}

       {/* Show course expired message if trying to schedule after expiry */}
       {isCourseExpired && (
         <Callout tone="danger" title="Course has expired">
           You cannot schedule a class after <strong>{courseExpiry}</strong>. Please extend the course
           expiry to continue scheduling.
         </Callout>
       )}
     </Dialog>
    )}

    {/* QA Response Modal */}
    <Dialog
      isOpen={showQaModal}
      onClose={() => setShowQaModal(false)}
      title="QA Transcript Status"
      actions={
        <Button variant="secondary" onClick={() => setShowQaModal(false)}>
          Close
        </Button>
      }
    >
        {qaResponse && (
          <>
            <DescriptionList
              items={[
                { label: "Status", value: <Badge tone={qaStatusTone}>{qaResponse.status}</Badge> },
                { label: "Message", value: qaResponse.message },
              ]}
            />

            {qaResponse.status === "ongoing" && (
              <Callout tone="warning" title="⏳ Processing in progress...">
                This may take 15-20 minutes. Please check back later.
              </Callout>
            )}

            {qaResponse.status === "done" && (
              <Callout tone="success" title="✅ Transcript is ready!" />
            )}
          </>
        )}
    </Dialog>

    {/* Zoom Link Modal */}
    <Dialog isOpen={showZoomModal} onClose={() => setShowZoomModal(false)} title="Zoom Links">
        {(zoomData.startUrl || zoomData.joinUrl) ? (
          <>
            {/* Teacher URL (startUrl) */}
            {zoomData.startUrl && (
              <div className="student-calendar-zoom">
                <span className="ds-field__label">Teacher URL</span>
                <div className="student-calendar-zoom__url student-calendar-zoom__url--clamp">
                  <a href={zoomData.startUrl} target="_blank" rel="noopener noreferrer">
                    {zoomData.startUrl}
                  </a>
                </div>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Copy size={13} aria-hidden />}
                    onClick={() => {
                      navigator.clipboard.writeText(zoomData.startUrl);
                      toast.success("Teacher URL copied to clipboard!");
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
              </div>
            )}

            {/* Student URL (joinUrl) — hidden for demo classes */}
            {shouldShowJoinUrl && zoomData.joinUrl && (
              <div className="student-calendar-zoom">
                <span className="ds-field__label">Student URL</span>
                <div className="student-calendar-zoom__url">
                  <a href={zoomData.joinUrl} target="_blank" rel="noopener noreferrer">
                    {zoomData.joinUrl}
                  </a>
                </div>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Copy size={13} aria-hidden />}
                    onClick={() => {
                      navigator.clipboard.writeText(zoomData.joinUrl);
                      toast.success("Student URL copied to clipboard!");
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyState title="No zoom links available" description="Generate the Zoom link again, or check that the class has a meeting." />
        )}
    </Dialog>
    <DemoRescheduleModal
      mode="reschedule"
      isOpen={showDemoRescheduleModal}
      toggle={() => setShowDemoRescheduleModal(false)}
      profileData={profileData}
      studentId={eventDetails?.studentId || studentId}
      classScheduleId={String(eventDetails?.classScheduleId || storedClassId || "")}
      eventDetails={eventDetails}
      courses={config?.courses || []}
      onSuccess={(reason) => {
        setShowDemoRescheduleModal(false);
        toggle();
        onReschedule(eventDetails?.classId, reason as any);
        fetchCourses();
      }}
    />
    <Dialog
      isOpen={showReassignTeacherModal}
      onClose={() => setShowReassignTeacherModal(false)}
      title="Book your slot"
      actions={
        <Button
          variant="primary"
          onClick={handleConfirmReassignTeacher}
          disabled={reassignSubmitting || !selectedReassignSlotId}
          loading={reassignSubmitting}
        >
          {reassignSubmitting ? "Submitting..." : "Submit"}
        </Button>
      }
    >
      <Field label="Select Teacher">
        {(ctl) => (
          <Select
            {...ctl}
            value={selectedReassignSlotId}
            onChange={(e) => setSelectedReassignSlotId(e.target.value)}
            disabled={reassignTeachersLoading}
            options={[
              { value: "", label: reassignTeachersLoading ? "Loading teachers..." : "Select Teacher" },
              ...reassignTeachers.map((entry: any) => ({
                value: String(entry?.id),
                label: String(getTeacherDisplayName(entry)),
              })),
            ]}
          />
        )}
      </Field>
    </Dialog>
    <Dialog
      isOpen={showDemoCancelModal}
      onClose={() => setShowDemoCancelModal(false)}
      title="Cancel Demo Class"
      backdrop="static"
      actions={
        <>
          <Button variant="secondary" onClick={() => setShowDemoCancelModal(false)}>
            Close
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDemoCancelClass}
            disabled={isCancellingDemoClass || !demoStudentCancelReason}
            loading={isCancellingDemoClass}
          >
            {isCancellingDemoClass ? "Cancelling..." : "Confirm Cancel"}
          </Button>
        </>
      }
    >
      <Field label="Student cancellation reason" id="demoStudentCancelReason">
        {(ctl) => (
          <Select
            {...ctl}
            value={demoStudentCancelReason}
            onChange={(e) => setDemoStudentCancelReason(e.target.value)}
            options={[
              { value: "", label: "Select student reason" },
              ...(config?.classCancellationReasonsForAdmin || []).map((reasonObj: any) => {
                const reason = reasonObj?.reason || "";
                return { value: reason, label: reason };
              }),
            ]}
          />
        )}
      </Field>
    </Dialog>

    <BookingFrequencyModal
      open={showBookingFrequency}
      selectedSlots={(bookingFrequencySlots.filter(Boolean) as any) || []}
      bookingContext={{
        studentId,
        studentUserId:
          localStorage.getItem("studentUserId") ||
          eventDetails?.studentUserId ||
          profileData?.userId ||
          profileData?.uId ||
          "",
        teacherUserId:
          eventDetails?.teacherUserId ||
          eventDetails?.teacherId ||
          localStorage.getItem("teacherUserId") ||
          "",
        courseId: localStorage.getItem("selectedCourse") || "",
        timezone: profileData?.timezone,
        studentName: [profileData?.firstName, profileData?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim(),
      }}
      onClose={() => {
        setShowBookingFrequency(false);
        setBookingFrequencySlots([]);
      }}
      onSuccess={handleBookingFrequencySuccess}
    />
   </div>
  );
};

export default ScheduledModal;
